// Considered: ContextCoverCardMolecule's former inline applicability block (SegmentedControl +
// MultiSelect + UnifiedFilterEditor mounted always-expanded in the cover card) — rejected as the
// long-term home because it reflows the cover card and is context-coupled; Phase 5 (SC3/SC12)
// extracts it into a rail-hosted, lane-gated editor reusable across template applicability
// surfaces (arc templates, approaches, channels — Phase 8). Not a fork: the property drill-in
// mirrors ContextLevelInspectorOrganism/SegmentPanelMolecule's established pattern. See spec
// context-selector-editor.md Phase 5 Reuse Audit.
import React, { useCallback, useMemo, useRef } from 'react';
import { isPropertyLeaf } from '@center-platform/spheres/filters';
import { useTranslation } from 'localization';
import { SegmentedControlAtom } from '../../atoms/SegmentedControl';
import { SphereTypeSelectMolecule } from '../SphereTypeSelect';
import type {
  SchemaSource,
  SphereTypeSelectMoleculeHandle,
  SphereTypeSelectParity,
} from '../SphereTypeSelect';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import AspectCatalogMolecule from '../AspectCatalog/AspectCatalogMolecule';
import UnifiedFilterTreeMolecule from '../UnifiedFilterTree/UnifiedFilterTreeMolecule';
import { withStableFilterGroupIds } from '../../../utils/filterGroupIds';
import { seedLeavesIntoGroups, topLevelRowToLeaf } from '../../../utils/filterGroupSeeding';
import { simpleGroupToUnified, unifiedGroupToSimple } from '../../../utils/contextFlowSerialization';
import type { FilterState } from '../../../types/filterState';
import type { PropertyOption, PropertyType } from '../PropertyFilter/PropertyFilterMolecule';
import type { PerspectiveOption } from '../PerspectiveFilter';
import type { RelationshipOption } from '../RelationshipFilter';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import type { ContextCoverDef } from '../../../types/contextFlow';
import type { OrgAvailableTypeSchema } from 'types/schema';
import type { FilterExpression, FilterGroup, FilterGroupLeaf } from 'types/filters';
import { nextFilterId, withStableIds } from '../../../utils/ids';
import {
  allowedLanesForSurface,
  mintFilterFromCatalogEntry,
  propertyOptionsToCatalogEntries,
} from '../../../utils/catalogBuilders';
import './ApplicabilityEditorMolecule.scss';

/**
 * Lane predicates (arc, relationship, perspective) require live graph traversal
 * and can never be evaluated by the synchronous server `validateApplicability`
 * (SC13) — a top-level lane makes it return `valid:false`, and a group-nested
 * `{kind}` leaf makes the group unevaluable. So an applicability filter carrying
 * one applies to NOTHING, silently. `allowedLanesForSurface('applicability')`
 * (catalogBuilders.ts — OQ-1 / Gap B) hides the lane sections below; this list
 * closes the two remaining doors into the same condition — the property picker
 * and the group-leaf picker — now that `filterPropertyBuilder` publishes all
 * three as pseudo-properties (EAINT-725 Phase 4: perspective is injected
 * unconditionally).
 */
const EXCLUDED_APPLICABILITY_PROPERTY_TYPES: PropertyType[] = ['arc', 'relationship', 'perspective'];

/** Routed through the shared predicate, not a component-local `allowedLanes={[]}`. */
const APPLICABILITY_LANE_AVAILABILITY = allowedLanesForSurface('applicability');

export type ApplicabilityMode = NonNullable<ContextCoverDef['applicabilityMode']>;

/**
 * Contract datum ids for a host whose decomposition covers this editor — the relationship
 * sheet's `<SphereScopeEditor>` (`studio/aspect-applicability.jsx:104-262`). Passed in rather
 * than stamped here, for the same reason `AgentThreadOrganism` takes its rail's: this molecule
 * is mounted by surfaces outside that decomposition, and their contracts name nothing here.
 */
export interface ApplicabilityEditorParity {
  /** The mode strip's first option. */
  modeOption?: string;
  /** The families-vs-types note under the picker. */
  familyHint?: string;
  /** The names carried over that this workspace's catalog cannot resolve. */
  unmapped?: string;
  /** The `Custom` mode named in the carried-over note's second line. */
  legacyMode?: string;
  /**
   * The `Specific types` footer's Done. It closes the picker panel, so the contract names it
   * with the picker — but the button belongs to this footer, not to `SphereTypeSelectMolecule`
   * (whose own panel footer this editor replaces).
   */
  done?: string;
  /** The picker's own datum ids. */
  typeSelect?: SphereTypeSelectParity;
  /**
   * The built-in condition catalog's scope-banner ids (used only when the host supplies no
   * `renderCatalog`): the searched-type count, the "N types" scope word, and the "no type in
   * scope" word.
   */
  catalog?: {
    searchingCount?: string;
    scopedCount?: string;
    coreScope?: string;
  };
}

/**
 * Seeds the interaction states this editor reaches by clicking. A story or a test declares
 * the reading it is showing instead of driving clicks to get there; nothing else passes it,
 * and the editor then opens with no drill-in and a closed picker as before.
 */
export interface ApplicabilityEditorInitialState {
  /** Which picker panel is open. */
  openAxis?: 'family' | 'type';
  /** What is typed in the picker's search. */
  query?: string;
  /** Family `schema_id`s folded away in the picker's TYPES axis. */
  collapsedFamilies?: string[];
}

/** Caller-supplied wording. See {@link ApplicabilityEditorMoleculeProps.copy}. */
export interface ApplicabilityEditorCopy {
  /** Replaces the context-worded "no filter yet" line in custom mode. */
  conditionsEmpty?: string;
  /** The families-vs-types note. Rendered only when supplied. */
  familyHint?: string;
  /** Replaces the type-list read-back, which otherwise names a CONTEXT's switcher. */
  specificHint?: (types: string) => string;
  /** Head of the carried-over callout. The callout renders only when this is supplied. */
  carriedOverTitle?: string;
  /** Rendered as `<strong>{names}</strong> {unmappedNote(count)}`. */
  unmappedNote?: (count: number) => string;
  /** Rendered as `{before} <strong>{mode}</strong> {after}` — the mode is a datum of its own. */
  legacyNote?: (rows: string[]) => { before: string; mode: string; after: string };
}

export interface ApplicabilityEditorMoleculeProps {
  mode: ApplicabilityMode;
  families: string[];
  types: string[];
  filter: FilterState;
  onModeChange: (mode: ApplicabilityMode) => void;
  onFamiliesChange: (families: string[]) => void;
  onTypesChange: (types: string[]) => void;
  onFilterChange: (filter: FilterState) => void;
  /** Properties available for the "specific types" shortcut + the "custom filter" property picker. */
  availableProperties: PropertyOption[];
  /**
   * The remaining lane catalogs — always injected (SC10 test note: injecting
   * none would make the `allowedLanes` gate below pass vacuously) but never
   * rendered, since `allowedLanes` gates perspective/relationship/arc/family
   * off unconditionally (`validateApplicability` can't evaluate any of them).
   */
  availablePerspectives?: PerspectiveOption[];
  availableRelationships?: RelationshipOption[];
  arcTemplates?: ArcTemplateOption[];
  familyTypeSchemas?: OrgAvailableTypeSchema[];
  /** Source classification for `familyTypeSchemas` (system_name → 'platform'|'org'|'approach'), e.g. from `useOrgAvailableTypes`. */
  sources?: Record<string, SchemaSource>;
  /** Which approach(es) provided each type — passed through to the type picker's source tag. */
  approachSources?: Record<string, string[]>;
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  readOnly?: boolean;
  /**
   * Parity self-description for the mode strip's first option (visual-fidelity.md).
   * A caller whose contract names this segmented — the relationship sheet's
   * `<SphereScopeEditor>` mode strip — passes the datum id; every other caller
   * leaves it unset and the strip renders exactly as before.
   *
   * @deprecated Prefer `parity.modeOption`, which carries the rest of the editor's ids too.
   */
  modeOptionDatum?: string;
  /** See {@link ApplicabilityEditorParity}. Unset outside the type composer. */
  parity?: ApplicabilityEditorParity;
  /**
   * Caller-supplied wording. The defaults describe a CONTEXT ("this context can be opened
   * from any sphere"), which is a false statement anywhere else — same reason `lead` and
   * `anyNote` exist. A caller that is not a context passes its own; every existing caller
   * omits this and reads exactly as before.
   */
  copy?: ApplicabilityEditorCopy;
  /** See {@link ApplicabilityEditorInitialState}. */
  initialState?: ApplicabilityEditorInitialState;
  /**
   * What this rule is FOR, in the caller's own words. The default copy names a context
   * ("which sphere types this context applies to"), which is a false statement anywhere
   * else — the relationship sheet's rule scopes a link's other end, not a context. A
   * caller that is not a context passes its own sentence; every existing caller omits it
   * and reads exactly as before.
   */
  lead?: string;
  /** Likewise for the `any` reading's consequence line. */
  anyNote?: string;
  /**
   * Whether the custom-filter tree offers nested AND/OR groups (`UnifiedFilterTreeMolecule`
   * `allowGroups`). The context inspector offers them; a caller whose contract is a flat
   * condition list (the relationship sheets' scope editor) passes `false`. Groups already
   * stored on the filter are still shown, never hidden.
   */
  allowFilterGroups?: boolean;
  /**
   * The custom-filter tree's host width (`UnifiedFilterTreeMolecule` `layout`). A rail or
   * sheet passes `'narrow'`: rows split onto two lines and "Add condition" opens the tree's
   * own drill-in instead of a floating catalog. Defaults to `'wide'`.
   */
  layout?: 'wide' | 'narrow';
  /**
   * Renders the "Add condition" catalog for the custom-filter tree's own
   * add-condition button — forwarded to `UnifiedFilterTreeMolecule`. The host
   * builds this against the SAME lane-filtered catalog
   * `APPLICABILITY_LANE_AVAILABILITY.catalogLanes` names (aspect + identity
   * only — no perspective/relationship/arc/measure entries, matching the
   * lanes withheld below). Omitted, the editor offers its own catalog built from
   * `availableProperties` (`renderDefaultCatalog`).
   */
  renderCatalog?: (opts: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => React.ReactNode;
}

/**
 * The condition catalog for a rule an applicability evaluator must be able to read: the
 * host's `availableProperties`, restricted to the lanes such a rule can evaluate
 * (`APPLICABILITY_LANE_AVAILABILITY.catalogLanes`, minus the lane pseudo-properties), with
 * the scope banner naming `scopeTypeLabels`. Returns a `renderCatalog` for
 * `UnifiedFilterTreeMolecule`. Shared by this editor and any host that authors conditions
 * onto such a rule directly (the relationship override sheet's extra conditions).
 */
export function useApplicabilityConditionCatalog({
  availableProperties,
  scopeTypeLabels,
  searchTotalTypeCount,
  parity,
}: {
  availableProperties: PropertyOption[];
  scopeTypeLabels: string[];
  searchTotalTypeCount?: number;
  parity?: ApplicabilityEditorParity['catalog'];
}) {
  const { t } = useTranslation();
  const entries = useMemo(
    () =>
      propertyOptionsToCatalogEntries(
        availableProperties.filter((p) => !EXCLUDED_APPLICABILITY_PROPERTY_TYPES.includes(p.type)),
        t
      ).filter((entry) => APPLICABILITY_LANE_AVAILABILITY.catalogLanes?.includes(entry.lane) ?? true),
    [availableProperties, t]
  );
  return useCallback(
    ({ onPick, onClose }: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => (
      <AspectCatalogMolecule
        entries={entries}
        scopeTypeLabels={scopeTypeLabels}
        searchTotalTypeCount={searchTotalTypeCount}
        searchTotalCountDatumId={parity?.searchingCount}
        scopeSomeBoldDatumId={parity?.scopedCount}
        scopeNoneBoldDatumId={parity?.coreScope}
        onPick={(entry) => {
          const minted = mintFilterFromCatalogEntry(entry);
          onPick(isPropertyLeaf(minted) ? { id: nextFilterId(), ...minted } : minted);
          onClose();
        }}
        onCancel={onClose}
      />
    ),
    [entries, scopeTypeLabels, searchTotalTypeCount, parity]
  );
}

const ApplicabilityEditorMolecule: React.FC<ApplicabilityEditorMoleculeProps> = ({
  mode,
  families,
  types,
  filter,
  onModeChange,
  onFamiliesChange,
  onTypesChange,
  onFilterChange,
  availableProperties,
  availablePerspectives = [],
  availableRelationships = [],
  arcTemplates = [],
  familyTypeSchemas = [],
  sources,
  approachSources,
  perspectiveSearchProps,
  readOnly = false,
  modeOptionDatum,
  parity,
  copy,
  initialState,
  lead,
  anyNote,
  allowFilterGroups = true,
  layout = 'wide',
  renderCatalog,
}) => {
  const { t } = useTranslation();
  const typeSelectRef = useRef<SphereTypeSelectMoleculeHandle>(null);

  const propertyFilters = useMemo(() => withStableIds(filter.propertyFilters ?? []), [filter]);

  // Lanes `validateApplicability` can't evaluate (SC13) may already be stored
  // (e.g. authored via MCP on a template surface) even though this editor can't
  // edit them. Never drop them on save — preserve verbatim, just warn (edge case
  // table: "preserves unknown lanes it can't edit, never drops").
  const hasUnavailableLanes =
    (filter.perspectiveFilters?.length ?? 0) > 0 ||
    (filter.relationshipFilters?.length ?? 0) > 0 ||
    (filter.arcFilters?.length ?? 0) > 0;

  // The custom-filter tree (everySurfaceMountsTheSameTree — no bespoke row list, no
  // second `UnifiedFilterEditor` mount for "advanced" groups). Top-level `propertyFilters`
  // fold into the root's own leaves (an AND group gets them appended; an OR root gets a
  // new leading AND group, same rule `AdvancedFilterEditorOrganism.deriveRoot`/`seedGroups`
  // use, so both hosts read one filter shape the same way). Perspective/relationship/arc
  // are NEVER folded in here — they stay on their own arrays, invisible to the tree and
  // untouched by it, per `hasUnavailableLanes` above.
  const rootGroup = useMemo<FilterGroup>(() => {
    const groups = filter.filterGroups ?? [];
    const seededGroups = seedLeavesIntoGroups(groups, propertyFilters.map(topLevelRowToLeaf));
    const stableGroups = withStableFilterGroupIds(seededGroups);
    if (stableGroups.length === 1) return simpleGroupToUnified(stableGroups[0]);
    return {
      id: nextFilterId(),
      operator: 'and',
      expressions: [],
      groups: stableGroups.map(simpleGroupToUnified),
    };
  }, [filter.filterGroups, propertyFilters]);

  /**
   * Every property condition on the rule, wherever the tree holds it — the tree writes its
   * leaves into `filterGroups`, so the top-level `propertyFilters` alone would miss them.
   */
  const propertyLeaves = useMemo(() => {
    const out: FilterExpression[] = [];
    const walk = (group: FilterGroup) => {
      for (const leaf of group.expressions) if (isPropertyLeaf(leaf)) out.push(leaf);
      for (const child of group.groups ?? []) walk(child);
    };
    walk(rootGroup);
    return out;
  }, [rootGroup]);

  const handleTreeChange = useCallback(
    (next: FilterGroup) => {
      onFilterChange({ ...filter, propertyFilters: [], filterGroups: [unifiedGroupToSimple(next)] });
    },
    [filter, onFilterChange]
  );

  // Resolve family/type system_names to display names for the read-back hint
  // (the picker itself renders removable chips; this line names them in prose).
  // Unknown system_names (a type since removed from the schema) render verbatim
  // rather than disappearing — the selection is preserved on save, so it must
  // stay visible.
  const nameBySystemName = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of familyTypeSchemas) m.set(s.system_name, s.name);
    return m;
  }, [familyTypeSchemas]);

  const specificReadBack = useMemo(() => {
    const selected = [...families, ...types];
    if (selected.length === 0) {
      return t('context.editor.inspector.applicability.hintSpecificAnyType');
    }
    return selected.map((v) => nameBySystemName.get(v) ?? v).join(', ');
  }, [families, types, nameBySystemName, t]);

  /**
   * Selections this workspace's catalog cannot resolve — a type the schema no longer has, or
   * a value written by another workspace. They are preserved on save, so they must be
   * VISIBLE: silently dropping them narrows the rule without saying so.
   */
  const unmappedNames = useMemo(
    () => [...families, ...types].filter((sn) => !nameBySystemName.has(sn)),
    [families, types, nameBySystemName]
  );

  /**
   * Conditions stored on the rule that the CURRENT mode does not apply — property filters
   * authored under Custom and left behind when the mode moved to a type list. Same reasoning
   * as `unmappedNames`: they are kept on save, so they are stated rather than hidden. The
   * lanes this editor cannot edit keep their own warning; they are not re-stated here.
   */
  const legacyRowLabels = useMemo(
    () =>
      propertyLeaves
        .filter((f) => f.property)
        .map((f) =>
          [f.property, f.operator, Array.isArray(f.values) ? f.values.join(', ') : f.value]
            .filter((part) => part !== undefined && part !== null && part !== '')
            .join(' ')
        ),
    [propertyLeaves]
  );

  /**
   * What the condition catalog is scoped to, in display names. The rule's own answer
   * is the scope: a type list names its types directly, and a Custom rule scopes itself
   * through its positive `type` / `label` conditions. An empty result is not "everything" —
   * it means the rule has not narrowed the other end yet, so the picker can only offer the
   * properties every sphere has, which is exactly what its unscoped banner says.
   */
  const scopeTypeKeys = useMemo(() => {
    const fromSelection = mode === 'specific' ? [...families, ...types] : [];
    const fromConditions =
      mode === 'custom'
        ? propertyLeaves
            .filter(
              (f) =>
                (f.property === 'type' || f.property === 'label') &&
                (f.operator === 'in' || f.operator === 'equals')
            )
            .flatMap((f) => (Array.isArray(f.values) ? f.values : [f.value]))
            .filter((v): v is string => typeof v === 'string' && v.length > 0)
        : [];
    return [...new Set([...fromSelection, ...fromConditions])];
  }, [mode, families, types, propertyLeaves]);

  const scopeTypeNames = useMemo(
    () => scopeTypeKeys.map((sn) => nameBySystemName.get(sn) ?? sn),
    [scopeTypeKeys, nameBySystemName]
  );

  /** The workspace's deployed type catalog — the size a search reaches past the scope. */
  const orgTypeCount = familyTypeSchemas.length;

  /** The tree's "Add condition" catalog when the host wires none. */
  const renderDefaultCatalog = useApplicabilityConditionCatalog({
    availableProperties,
    scopeTypeLabels: scopeTypeNames,
    searchTotalTypeCount: orgTypeCount,
    parity: parity?.catalog,
  });

  const modeDatum = parity?.modeOption ?? modeOptionDatum;
  const modeOptions: Array<{ value: ApplicabilityMode; label: string; datum?: string }> = [
    { value: 'any', label: t('context.editor.applicabilityAllTypes'), ...(modeDatum ? { datum: modeDatum } : {}) },
    { value: 'specific', label: t('context.editor.applicabilitySpecificTypes') },
    { value: 'custom', label: t('context.editor.applicabilityCustomFilter') },
  ];

  return (
    <div className="applicability-editor-molecule" data-testid="applicability-editor">
      <p className="applicability-editor-molecule__description">
        {lead ?? t('context.editor.applicabilityDescription')}
      </p>

      <SegmentedControlAtom
        options={modeOptions}
        value={mode}
        onChange={(v) => !readOnly && onModeChange(v as ApplicabilityMode)}
        mode="single"
        size="small"
        renderAs={modeDatum ? 'button' : 'input'}
        optionKind={modeDatum ? 'button' : undefined}
        ariaLabel={t('context.editor.applicabilityTitle')}
      />

      {mode === 'any' && (
        <div className="applicability-editor-molecule__hint" data-testid="applicability-hint-any">
          {anyNote ?? t('context.editor.inspector.applicability.hintAny')}
        </div>
      )}

      {mode === 'specific' && (
        <div className="applicability-editor-molecule__detail applicability-editor-molecule__detail--with-footer">
          <div className="applicability-editor-molecule__detail-content">
            <SphereTypeSelectMolecule
              ref={typeSelectRef}
              schemas={familyTypeSchemas}
              selectedFamilies={families}
              selectedTypes={types}
              onFamiliesChange={onFamiliesChange}
              onTypesChange={onTypesChange}
              sources={sources}
              approachSources={approachSources}
              readOnly={readOnly}
              parity={parity?.typeSelect}
              initialOpenAxis={initialState?.openAxis}
              initialQuery={initialState?.query}
              initialCollapsedFamilies={initialState?.collapsedFamilies}
              className="applicability-editor-molecule__type-picker"
            />
            {/* Families and types answer the same question differently, and the
                difference only matters once you are choosing. Stated here, not in the
                mode strip, so it is read at the moment it applies. */}
            {copy?.familyHint && (
              <div
                className="applicability-editor-molecule__family-hint"
                data-datum={parity?.familyHint}
                data-kind={parity?.familyHint ? 'hint' : undefined}
                data-testid="applicability-family-hint"
              >
                {copy.familyHint}
              </div>
            )}
            {copy?.carriedOverTitle && (unmappedNames.length > 0 || legacyRowLabels.length > 0) && (
              <div
                className="applicability-editor-molecule__carried-over"
                data-testid="applicability-carried-over"
              >
                <div className="applicability-editor-molecule__carried-over-title">
                  <i className="pi pi-exclamation-triangle" aria-hidden="true" />
                  {copy.carriedOverTitle}
                </div>
                {unmappedNames.length > 0 && copy.unmappedNote && (
                  <p className="applicability-editor-molecule__carried-over-note">
                    <strong data-datum={parity?.unmapped} data-kind={parity?.unmapped ? 'strong' : undefined}>
                      {unmappedNames.join(', ')}
                    </strong>{' '}
                    {copy.unmappedNote(unmappedNames.length)}
                  </p>
                )}
                {legacyRowLabels.length > 0 && copy.legacyNote && (() => {
                  const note = copy.legacyNote(legacyRowLabels);
                  return (
                    <p className="applicability-editor-molecule__carried-over-note">
                      {note.before}{' '}
                      <strong
                        data-datum={parity?.legacyMode}
                        data-kind={parity?.legacyMode ? 'strong' : undefined}
                      >
                        {note.mode}
                      </strong>{' '}
                      {note.after}
                    </p>
                  );
                })()}
              </div>
            )}
          </div>
          <div className="applicability-editor-molecule__footer">
            <div className="applicability-editor-molecule__footer-left">
              <div className="applicability-editor-molecule__footer-counter">
                {(families.length > 0 || types.length > 0) && (
                  <span>
                    {[
                      families.length > 0 &&
                        `${families.length} ${families.length === 1 ? 'family' : 'families'}`,
                      types.length > 0 &&
                        `${types.length} ${types.length === 1 ? 'type' : 'types'}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                )}
              </div>
              <div className="applicability-editor-molecule__footer-hint" data-testid="applicability-hint-specific">
                {copy?.specificHint?.(specificReadBack) ??
                  t('context.editor.inspector.applicability.hintSpecific', { types: specificReadBack })}
              </div>
            </div>
            {!readOnly && (
              <ButtonAtom
                variant="primary"
                size="small"
                className="applicability-editor-molecule__footer-done"
                onClick={() => typeSelectRef.current?.closePanel()}
                data-testid="applicability-specific-done"
                data-datum={parity?.done}
                label={t('sphereTypeSelect.done')}
              />
            )}
          </div>
        </div>
      )}

      {mode === 'custom' && (
        <div className="applicability-editor-molecule__detail">
          {APPLICABILITY_LANE_AVAILABILITY.reasonKey && (
            <MessageAtom
              severity="info"
              className="applicability-editor-molecule__lanes-unavailable-reason"
              data-testid="applicability-lanes-unavailable-reason"
              text={t(APPLICABILITY_LANE_AVAILABILITY.reasonKey)}
            />
          )}
          {hasUnavailableLanes && (
            <MessageAtom
              severity="warn"
              className="applicability-editor-molecule__unavailable-warning"
              data-testid="applicability-unavailable-warning"
              text={t('context.editor.inspector.applicability.unavailableLanesWarning')}
            />
          )}

          {/* The same canonical tree every other surface mounts (everySurfaceMountsTheSameTree)
              — property leaves and nested AND/OR groups in one composer. `renderCatalog`'s
              catalog is lane-restricted to aspect + identity (`APPLICABILITY_LANE_AVAILABILITY
              .catalogLanes`) — by the host, or by `renderDefaultCatalog` when none is wired — matching `excludePropertyTypes` below; perspective/
              relationship/arc leaves never reach this tree (SC10/SC13) — they stay on their
              own arrays, surfaced only by the warning above. */}
          <UnifiedFilterTreeMolecule
            value={rootGroup}
            onChange={handleTreeChange}
            availableProperties={availableProperties}
            excludePropertyTypes={EXCLUDED_APPLICABILITY_PROPERTY_TYPES}
            arcTemplates={arcTemplates}
            availableRelationships={availableRelationships}
            availablePerspectives={availablePerspectives}
            familyTypeSchemas={familyTypeSchemas}
            perspectiveSearchProps={perspectiveSearchProps}
            renderCatalog={renderCatalog ?? renderDefaultCatalog}
            readOnly={readOnly}
            allowGroups={allowFilterGroups}
            layout={layout}
            emptyNote={copy?.conditionsEmpty ?? t('context.editor.inspector.applicability.filtersEmpty')}
            datumScope="applicability-filter-tree"
            container="ui-applicability"
            className="applicability-editor-molecule__filter-tree"
            dnd
            defaultOpen
          />
        </div>
      )}
    </div>
  );
};

export default ApplicabilityEditorMolecule;
